// Libs
import React, { useMemo } from 'react';
// Utils
import { filterOption } from 'utils/libs';
// Hooks
import {
	useGenerateReport,
	useGenerateReportSubmit,
} from 'modules/Reports/hooks';
// Components
import { DefaultForm } from 'core/FormBuilder';
import {
	Wrapper,
	Button,
	TextField,
	Divider,
	DatePicker,
	TimePicker,
	Span,
} from 'components';
// Styles
import styles from './Generate.module.css';
import Select from 'components/Select';
import {
	buildGenerateReportPayload,
	buildReportParams,
	getReportMetadata,
	isCardexProjectReport,
} from './reportContract';
import LegacyReportFilters from './LegacyReportFilters';
import useCardexMaterialOptions from './useCardexMaterialOptions';

const { Option } = Select;
const { TextArea, Input } = TextField;

const isRequiredParam = param => !!param?.required;

// AntD parses a clock-only entry on today's date. Keep the other part of the
// report's datetime when either picker changes, without mutating either Moment.
const mergeDateTimePart = (value, previousValue, part) => {
	if (
		!value ||
		!previousValue ||
		typeof value.clone !== 'function' ||
		typeof previousValue.clone !== 'function'
	)
		return value;
	const result = previousValue.clone();
	if (part === 'date')
		return result.year(value.year()).month(value.month()).date(value.date());
	return result
		.hour(value.hour())
		.minute(value.minute())
		.second(value.second())
		.millisecond(value.millisecond());
};

const Generate = ({
	isMobile,
	form,
	values,
	actionPayload,
	setValues,
	submitForm,
	getProcessedComponentSqlData,
	onSuccess,
}) => {
	const { reportId, reportTable } = actionPayload ?? {};
	const { reportParams: declaredReportParams, reportProps } =
		getReportMetadata(actionPayload);
	const reportParams = useMemo(
		() => buildReportParams(form.components, declaredReportParams),
		[form.components, declaredReportParams]
	);
	const { users } = useGenerateReport();
	const { submitReport, isLoading } = useGenerateReportSubmit();
	const projectScoped = isCardexProjectReport(
		reportId,
		form.components,
		declaredReportParams
	);
	const materials = useCardexMaterialOptions({
		enabled: projectScoped,
		reportId,
		reportParams: declaredReportParams,
		components: form.components,
		values,
		setValues,
	});
	const displayedForm = projectScoped
		? {
				...form,
				components: form.components.filter(
					component => component !== materials.component
				),
		  }
		: form;
	const onGenerateReportClick = () =>
		!materials.blocked &&
		submitReport({
			payload: buildGenerateReportPayload({
				reportId,
				reportTable,
				values,
				reportParams: declaredReportParams,
				components: form.components,
			}),
			reportParams,
			onSuccess,
		});
	const legacyFilters = (
		<LegacyReportFilters
			reportId={reportId}
			components={form.components}
			reportParams={declaredReportParams}
			values={values}
			setValues={setValues}
		/>
	);

	return (
		<div className={styles.container}>
			<Divider color='gray' orientation='left' style={{ margin: '16px 0 0 0' }}>
				Filtros del Reporte
			</Divider>

			{projectScoped && legacyFilters}
			<DefaultForm
				isMobile={isMobile}
				form={displayedForm}
				values={values}
				setValues={setValues}
				submitForm={submitForm}
				getProcessedComponentSqlData={getProcessedComponentSqlData}
			/>

			{!projectScoped && legacyFilters}
			{projectScoped && (
				<>
					<Select
						name={materials.component.param}
						placeholder='Artículos*'
						mode='multiple'
						margin='20px 0 0 0'
						width='100%'
						showSearch
						autoClearSearchValue={false}
						searchValue={materials.searchValue}
						filterOption={false}
						loading={materials.loading}
						disabled={materials.fieldDisabled}
						value={materials.value}
						onSearch={materials.searchOptions}
						onChange={materials.onChange}
					>
						{materials.options.map(option => (
							<Option key={option.id} value={option.id}>
								{option.name}
							</Option>
						))}
					</Select>
					{materials.error && (
						<Wrapper padding='0' flexDirection='column'>
							<Span>No se pudieron cargar los artículos.</Span>
							<Button onClick={materials.retry}>Reintentar artículos</Button>
						</Wrapper>
					)}
				</>
			)}

			{/* FROM DATE */}
			{reportParams.fromDateParam && (
				<Wrapper padding='0' alignItems='baseline' width='100%'>
					<DatePicker
						style={{ width: '100%', marginTop: '20px' }}
						placeholder={
							isRequiredParam(reportParams.fromDateParam) ? 'Desde*' : 'Desde'
						}
						value={values.fromDateParam}
						onChange={value =>
							setValues(prev => ({
								...prev,
								fromDateParam:
									reportParams.fromDateParam.type === 'datetime'
										? mergeDateTimePart(value, prev.fromDateParam, 'date')
										: value,
							}))
						}
					/>
					{reportParams.fromDateParam.type === 'datetime' && (
						<TimePicker
							style={{ width: '100%', marginTop: '10px' }}
							allowClear={false}
							value={values.fromDateParam}
							onChange={value =>
								setValues(prev => ({
									...prev,
									fromDateParam: mergeDateTimePart(
										value,
										prev.fromDateParam,
										'time'
									),
								}))
							}
						/>
					)}
				</Wrapper>
			)}

			{/* TO DATE */}
			{reportParams.toDateParam && (
				<Wrapper padding='0' alignItems='baseline' width='100%'>
					<DatePicker
						placeholder={
							isRequiredParam(reportParams.toDateParam) ? 'Hasta*' : 'Hasta'
						}
						style={{ width: '100%', marginTop: '10px' }}
						value={values.toDateParam}
						onChange={value =>
							setValues(prev => ({
								...prev,
								toDateParam:
									reportParams.toDateParam.type === 'datetime'
										? mergeDateTimePart(value, prev.toDateParam, 'date')
										: value,
							}))
						}
					/>

					{reportParams.toDateParam.type === 'datetime' && (
						<TimePicker
							style={{ width: '100%', marginTop: '10px' }}
							allowClear={false}
							value={values.toDateParam}
							onChange={value =>
								setValues(prev => ({
									...prev,
									toDateParam: mergeDateTimePart(
										value,
										prev.toDateParam,
										'time'
									),
								}))
							}
						/>
					)}
				</Wrapper>
			)}

			<Divider color='gray' orientation='left' style={{ margin: '32px 0 0 0' }}>
				Detalle del Correo
			</Divider>

			{/* TO EMAILS */}
			<Select
				placeholder='Destinatarios*'
				mode='tags'
				margin='20px 0 0 0'
				width='100%'
				showSearch
				value={values.toEmails}
				filterOption={filterOption}
				onChange={value => setValues(prev => ({ ...prev, toEmails: value }))}
			>
				{users
					.filter(
						usr => !values.ccEmails || values.ccEmails.indexOf(usr.email) === -1
					)
					.map((user, idx) => (
						<Option key={idx} value={user.email}>
							{user.name}
						</Option>
					))}
			</Select>

			{/* CC EMAILS */}
			<Select
				placeholder='Con copia a...'
				mode='tags'
				margin='10px 0 0 0'
				width='100%'
				showSearch
				value={values.ccEmails}
				filterOption={filterOption}
				onChange={value => setValues(prev => ({ ...prev, ccEmails: value }))}
			>
				{users
					.filter(
						usr => !values.toEmails || values.toEmails.indexOf(usr.email) === -1
					)
					.map((user, idx) => (
						<Option key={idx} value={user.email}>
							{user.name}
						</Option>
					))}
			</Select>

			{/* SUBJECT */}
			<Input
				margin='10px 0 0 0'
				required
				placeholder='Asunto'
				name='subject'
				value={values.subject ?? reportProps.subject}
				onChange={e =>
					setValues(prev => ({ ...prev, subject: e.target.value }))
				}
			/>

			{/* COMMENT */}
			<TextArea
				margin='10px 0 0 0'
				placeholder='Mensaje'
				rows={3}
				value={values.message ?? reportProps.text}
				onChange={e =>
					setValues(prev => ({ ...prev, message: e.target.value }))
				}
			/>

			<Wrapper width='100%' justifyContent='center'>
				<Button
					margin='10px 0 0 0'
					mode='primary'
					disabled={isLoading || materials.blocked}
					onClick={onGenerateReportClick}
				>
					{isLoading ? 'Generando...' : 'Generar'}
				</Button>
			</Wrapper>
		</div>
	);
};

export default Generate;
